import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import AppLayout from '@/layouts/AppLayout'
import RequireAuth from '@/components/RequireAuth'
import PublicRoute from '@/components/PublicRoute'
import PageLoading from '@/components/PageLoading'

// Lazy load pages for better performance
const Login = lazy(() => import('@/pages/Login'))
const Register = lazy(() => import('@/pages/Register'))
const Privacy = lazy(() => import('@/pages/Privacy'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Orders = lazy(() => import('@/pages/Orders'))
const OrderDetail = lazy(() => import('@/pages/OrderDetail'))
const Inspection = lazy(() => import('@/pages/Inspection'))
const Settings = lazy(() => import('@/pages/Settings'))
const UserManagement = lazy(() => import('@/pages/UserManagement'))
const UserApproval = lazy(() => import('@/pages/UserApproval'))
const PermissionSettings = lazy(() => import('@/pages/PermissionSettings'))
const ApiCredentials = lazy(() => import('@/pages/ApiCredentials'))
const Trash = lazy(() => import('@/pages/Trash'))
const Cancelled = lazy(() => import('@/pages/Cancelled'))
const Verification = lazy(() => import('@/pages/Verification'))
const ProductSearch = lazy(() => import('@/pages/ProductSearch'))

// Inventory pages
const Products = lazy(() => import('@/pages/inventory/ProductsSimpleFixed'))
const ProductDetail = lazy(() => import('@/pages/inventory/ProductDetail'))
const Inbound = lazy(() => import('@/pages/inventory/Inbound'))
const Outbound = lazy(() => import('@/pages/inventory/Outbound'))
const StockHistory = lazy(() => import('@/pages/inventory/StockHistory'))
const Analysis = lazy(() => import('@/pages/inventory/AnalysisNew'))
const PastQuantity = lazy(() => import('@/pages/inventory/PastQuantity'))
const Adjustment = lazy(() => import('@/pages/inventory/Adjustment'))
const BarcodePrint = lazy(() => import('@/pages/inventory/BarcodePrint'))
const TestPage = lazy(() => import('@/pages/inventory/TestPage'))
const DataManagement = lazy(() => import('@/pages/inventory/DataManagement'))
const BarcodeMapping = lazy(() => import('@/pages/inventory/BarcodeMapping'))
const StopSale = lazy(() => import('@/pages/inventory/StopSale'))  // 판매중지/재입고 알림 (2026-10-22)

// Purchase pages
const PurchaseNeeded = lazy(() => import('@/pages/purchase/PurchaseNeeded'))
const PurchaseOrders = lazy(() => import('@/pages/purchase/PurchaseOrders'))
const PurchaseOrderDetail = lazy(() => import('@/pages/purchase/PurchaseOrderDetail'))
const IncomingInventory = lazy(() => import('@/pages/purchase/IncomingInventory'))
const Suppliers = lazy(() => import('@/pages/purchase/Suppliers'))
const SupplierDetail = lazy(() => import('@/pages/purchase/SupplierDetail'))
const PurchaseApproval = lazy(() => import('@/pages/purchase/PurchaseApproval'))
const PriceManagement = lazy(() => import('@/pages/purchase/PriceManagement'))
const PurchaseDashboard = lazy(() => import('@/pages/purchase/PurchaseDashboard'))
const InternetPurchase = lazy(() => import('@/pages/purchase/InternetPurchase'))

// Dashboard pages
const OrderDashboard = lazy(() => import('@/pages/orders/OrderDashboard'))
const InventoryDashboard = lazy(() => import('@/pages/inventory/InventoryDashboard'))

// Marketplace pages (super_admin only)
const MarketplaceDashboard = lazy(() => import('@/pages/marketplace/MarketplaceDashboard'))
const AccountList = lazy(() => import('@/pages/marketplace/AccountList'))
const OrderCollection = lazy(() => import('@/pages/marketplace/OrderCollection'))
const TrackingUpload = lazy(() => import('@/pages/marketplace/TrackingUpload'))
const ProductMapping = lazy(() => import('@/pages/marketplace/ProductMapping'))
const ProductRegister = lazy(() => import('@/pages/marketplace/ProductRegister'))
const StockSync = lazy(() => import('@/pages/marketplace/StockSync'))
const ExtraInfo = lazy(() => import('@/pages/marketplace/ExtraInfo'))
const CategoryMapping = lazy(() => import('@/pages/marketplace/CategoryMapping'))
const AttributeManagement = lazy(() => import('@/pages/marketplace/AttributeManagement'))
const ScheduledRegister = lazy(() => import('@/pages/marketplace/ScheduledRegister'))
const RegisterFailed = lazy(() => import('@/pages/marketplace/RegisterFailed'))
const ProductEdit = lazy(() => import('@/pages/marketplace/ProductEdit'))
const SoldoutManagement = lazy(() => import('@/pages/marketplace/SoldoutManagement'))
const ManualOrderInput = lazy(() => import('@/pages/marketplace/ManualOrderInput'))
const OrderHistory = lazy(() => import('@/pages/marketplace/OrderHistory'))
const CodeMapping = lazy(() => import('@/pages/marketplace/CodeMapping'))
const BatchTracking = lazy(() => import('@/pages/marketplace/BatchTracking'))
const TrackingHistory = lazy(() => import('@/pages/marketplace/TrackingHistory'))
const TrackingFailed = lazy(() => import('@/pages/marketplace/TrackingFailed'))
const Scheduler = lazy(() => import('@/pages/marketplace/Scheduler'))
const SchedulerLogs = lazy(() => import('@/pages/marketplace/SchedulerLogs'))

// Analysis pages
const SalesSummary       = lazy(() => import('@/pages/analysis/SalesSummary'))
const SalesAnalysis      = lazy(() => import('@/pages/analysis/SalesAnalysis'))
const MarginCalculation  = lazy(() => import('@/pages/analysis/MarginCalculation'))
const AnalysisSettings   = lazy(() => import('@/pages/analysis/AnalysisSettings'))
const MetawellMarketing  = lazy(() => import('@/pages/analysis/MetawellMarketing'))
const NppipMarketing     = lazy(() => import('@/pages/analysis/NppipMarketing'))

// Admin pages
const WeeklyAnalysis  = lazy(() => import('@/pages/admin/WeeklyAnalysis'))
const MonthlyAnalysis = lazy(() => import('@/pages/admin/MonthlyAnalysis'))
const BlogLogic = lazy(() => import('@/pages/admin/BlogLogic'))

// Analyze 보안 게이트 (URL 직접 진입만, 메뉴 노출 X)
const Analyze = lazy(() => import('@/pages/Analyze'))
import { AnalyzeProtected } from '@/pages/Analyze'

export default function AppRoutes() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/privacy" element={<Privacy />} />
        
        {/* Protected routes */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/orders" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          
          <Route path="orders">
            <Route index element={<Orders />} />
            <Route path="dashboard" element={<OrderDashboard />} />
            <Route path="pending" element={<Orders status="pending" />} />
            <Route path="verification" element={<Verification />} />
            <Route path="inspected" element={<Orders status="inspected" />} />
            <Route path="completed" element={<Orders status="completed" />} />
            <Route path="cancelled" element={<Cancelled />} />
            <Route path="prepacked" element={<Orders status="prepacked" />} />
            <Route path="split-delivery" element={<Orders status="split_delivery" />} />
            <Route path="unregistered" element={<Orders status="unregistered" />} />
            <Route path="3pl" element={<Orders status="3pl" />} />
            <Route path="problem" element={<Orders status="problem" />} />
            <Route path="hold" element={<Orders status="hold" />} />
            <Route path="trash" element={<Trash />} />
            <Route path="inspection" element={<Inspection />} />
            <Route path="product-search" element={<ProductSearch />} />
            <Route path=":orderNumber" element={<OrderDetail />} />
          </Route>

          <Route path="inventory">
            <Route index element={<Navigate to="/inventory/products" replace />} />
            <Route path="dashboard" element={<InventoryDashboard />} />
            <Route path="products" element={<Products />} />
            <Route path="products/new" element={<ProductDetail key="new" />} />
            <Route path="products/:id" element={<ProductDetail key="detail" />} />
            <Route path="inbound" element={<Inbound />} />
            <Route path="outbound" element={<Outbound />} />
            <Route path="history" element={<StockHistory />} />
            <Route path="analysis" element={<Analysis />} />
            <Route path="past-quantity" element={<PastQuantity />} />
            <Route path="adjustment" element={<Adjustment />} />
            <Route path="barcode-print" element={<BarcodePrint />} />
            <Route path="barcode-mapping" element={<BarcodeMapping />} />
            <Route path="stop-sale" element={<StopSale />} />
            <Route path="data-management" element={<DataManagement />} />
            <Route path="test" element={<TestPage />} />
          </Route>

          <Route path="purchase">
            <Route index element={<Navigate to="/purchase/needed" replace />} />
            <Route path="dashboard" element={<PurchaseDashboard />} />
            <Route path="needed" element={<PurchaseNeeded />} />
            <Route path="approval" element={<PurchaseApproval />} />
            <Route path="orders" element={<PurchaseOrders />} />
            <Route path="orders/:id" element={<PurchaseOrderDetail />} />
            <Route path="incoming" element={<IncomingInventory />} />
            <Route path="internet" element={<InternetPurchase />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="suppliers/:id" element={<SupplierDetail />} />
            <Route path="price-management" element={<PriceManagement />} />
          </Route>

          <Route path="settings">
            <Route index element={<Settings />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="approval" element={<UserApproval />} />
            <Route path="permissions" element={<PermissionSettings />} />
            <Route path="api-keys" element={<ApiCredentials />} />
            <Route path="admin">
              <Route path="weekly"  element={<AnalyzeProtected><WeeklyAnalysis /></AnalyzeProtected>} />
              <Route path="monthly" element={<AnalyzeProtected><MonthlyAnalysis /></AnalyzeProtected>} />
              <Route path="blog-logic" element={<AnalyzeProtected><BlogLogic /></AnalyzeProtected>} />
            </Route>
          </Route>

          {/* 보안 메뉴 진입 게이트 (URL 직접 입력만, 메뉴 노출 X) */}
          <Route path="analyze" element={<Analyze />} />

          {/* Marketplace routes (super_admin only) */}
          <Route path="marketplace">
            <Route index element={<Navigate to="/marketplace/dashboard" replace />} />
            {/* 쇼핑몰관리 */}
            <Route path="dashboard" element={<MarketplaceDashboard />} />
            <Route path="accounts" element={<AccountList />} />
            <Route path="extra-info" element={<ExtraInfo />} />
            {/* 쇼핑몰카테고리 */}
            <Route path="categories" element={<CategoryMapping />} />
            <Route path="attributes" element={<AttributeManagement />} />
            {/* 쇼핑몰상품등록 */}
            <Route path="register" element={<ProductRegister />} />
            <Route path="register/scheduled" element={<ScheduledRegister />} />
            <Route path="register/failed" element={<RegisterFailed />} />
            {/* 쇼핑몰상품관리 */}
            <Route path="products" element={<ProductMapping />} />
            <Route path="products/edit" element={<ProductEdit />} />
            <Route path="products/soldout" element={<SoldoutManagement />} />
            <Route path="stock-sync" element={<StockSync />} />
            {/* 주문수집 */}
            <Route path="orders" element={<OrderCollection />} />
            <Route path="orders/manual" element={<ManualOrderInput />} />
            <Route path="orders/history" element={<OrderHistory />} />
            <Route path="code-mapping" element={<CodeMapping />} />
            {/* 송장관리 */}
            <Route path="tracking" element={<TrackingUpload />} />
            <Route path="tracking/batch" element={<BatchTracking />} />
            <Route path="tracking/history" element={<TrackingHistory />} />
            <Route path="tracking/failed" element={<TrackingFailed />} />
            {/* 자동화/스케줄러 */}
            <Route path="scheduler" element={<Scheduler />} />
            <Route path="scheduler/logs" element={<SchedulerLogs />} />
          </Route>
          {/* Analysis routes */}
          <Route path="analysis">
            <Route path="japan/sales/summary"  element={<SalesSummary channel="일본직구" />} />
            <Route path="japan/sales/analysis" element={<SalesAnalysis key="일본직구" channel="일본직구" />} />
            <Route path="japan/sales/margin"   element={<MarginCalculation channel="일본직구" />} />
            <Route path="japan/marketing"      element={<NppipMarketing />} />
            <Route path="metawell/sales/summary"  element={<SalesSummary channel="메타웰" />} />
            <Route path="metawell/sales/analysis" element={<SalesAnalysis key="메타웰" channel="메타웰" />} />
            <Route path="metawell/sales/margin"   element={<MarginCalculation channel="메타웰" />} />
            <Route path="metawell/marketing"      element={<MetawellMarketing />} />
            <Route path="settings" element={<AnalysisSettings />} />
          </Route>
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  )
}